import { Component } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { FileManagerService } from '../../../services/file-manager.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { scheduleCoordinator } from '../../../services/scheduling/schedule-coordinator';
import { SpinnerService } from '../../../services/spinner.service';
import { StorageService } from '../../../services/storage.service';
import { UIContext } from '../../../services/ui-context';
import { RouteProps } from '../../../types/route-types';
import { FileListItemComponent } from './file-list-item.component';
import ContactUsModal from '../contact-us-modal/contact-us.modal';
import * as styles from './file-list.component.module.scss'
import { MotiveComponent } from '../motive/motive.component';
import { AdSlots } from '../../ui/motive/ad-slots';
import { MotiveLocationTypes } from '../../../utils/request.utils';

interface FileListProps extends RouteProps {
    parentClasses: string;
    cardClasses: string;
    fileUploadArray: FileUpload[];
    sectionTitle;
}


/** Review 7 M1-03: what the batch row says once every file has started (it no longer disappears then). */
export const SCHEDULE_ALL_STARTED_COPY = 'All your files have started';

export class FileListComponent extends Component<FileListProps> {
    private readonly logger = Logger.getLogger('FileListComponent');

    /** Teardown handle for the hands-off scheduling subscription. */
    private unsubscribeSchedule: (() => void) | null = null;

    componentDidMount() {
        // The batch header counts and the "Retry all" control are driven by a
        // timer-backed state machine, not by props, so the header has to hear
        // about transitions from the coordinator (2026-09-05 Mongo write-stall
        // incident; MONGO-STALL-PLAN-v5.2.md §4.4). Inert while the flag is off.
        this.unsubscribeSchedule = scheduleCoordinator.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount() {
        if (this.unsubscribeSchedule) {
            this.unsubscribeSchedule();
            this.unsubscribeSchedule = null;
        }
    }

    /**
     * "N queued · M processing · K waiting", plus the ONE batch-level Retry all.
     *
     * WHY (plan §4.4, reviewer finding SaaS #5): during the 2026-09-05 stall a
     * 340-file batch had no batch-level anything — each card failed on its own
     * and each failure opened its own modal after deleting its own uploaded
     * bytes. One row, one control, at most one toast.
     *
     * The counts are the coordinator's, not the list's: `queued` is cards
     * waiting for the edge to start, `processing` is cards it has started,
     * `waiting` is the depth of the 4-slot schedule gate — the files whose
     * schedule call has not been SENT yet, either behind the cap or behind the
     * shared Retry-After pause.
     */
    /** Review 7 M1-03: the batch row has been on screen in this mount, so it keeps its place (see `getScheduleHeader`). */
    private scheduleHeaderShown = false;

    private getScheduleHeader() {
        if (!scheduleCoordinator.enabled) return null;
        const summary = scheduleCoordinator.summary();
        const idle = summary.queued === 0 && summary.stalled === 0 && summary.waiting === 0;
        // Review 7 M1-03: the row used to unmount the moment the last file started, and every card below jumped up 57 px just
        // as its Download key was about to turn live. Once shown on this page it stays, and says the batch has started.
        if (idle && !this.scheduleHeaderShown) return null;
        this.scheduleHeaderShown = true;
        return <div className={styles['schedule-batch-header']} data-jkey="schedule-batch-header" role="status">
            <span className={styles['schedule-batch-counts']}>
                {idle
                    ? SCHEDULE_ALL_STARTED_COPY
                    : `${summary.queued} queued · ${summary.processing} processing · ${summary.waiting} waiting`}
                {!idle && summary.paused ? ' · paused by the server' : ''}
            </span>
            {
                summary.stalled > 0 && <button
                    className={`${styles['btn']} ${styles['schedule-retry-all']}`}
                    data-jkey="schedule-retry-all"
                    onClick={() => scheduleCoordinator.retryAll()}>
                    {`Retry all (${summary.stalled})`}
                </button>
            }
        </div>;
    }

    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(StorageService) private storageService: StorageService;

    async clearAllUserData() {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Clear All User Data',
            `Are you sure you want to remove all user data and refresh this page?`,
            () => {
                this.storageService.clearAll();
                window.location.reload();
            },
        );
    }

    async removeAll(fileUploadArray: FileUpload[]) {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Delete File',
            `Are you sure you want to delete all files listed on ${this.props.sectionTitle}?`,
            async () => {
                for (const fileUpload of fileUploadArray) {
                    await this.fileManagerService.removeFile(fileUpload);
                }
            },
        );
    }

    async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: `Something went wrong while loading scripts. ${e.message}`, error: e });
            }
        }, 'Please wait while we initialize file upload service...');
    }

    render() {
        return this.props.fileUploadArray && this.props.fileUploadArray.length !== 0 && <div className={`${this.props.parentClasses}`}>

            <div className={styles['section-header']} >
                <h4 className={styles['page-title']}>
                    {this.props.sectionTitle}
                </h4>
                <div className={styles['action-buttons']} >
                    <a className={styles['help-link']} href="#" onClick={(e) => { e.preventDefault(); ContactUsModal.show({}); }}>Need help?</a>
                    <button className={styles['btn']} onClick={() => this.clearAllUserData()
                    }>
                        Clear All
                    </button >
                    <button className={`${styles['btn']} ${styles['btn-danger']}`} onClick={() => this.removeAll(this.props.fileUploadArray)}>
                        Delete All
                    </button >
                </div >
            </div>
            {this.getScheduleHeader()}
            <div className={styles['downloadable-items']} >
                <div className={styles['left-panel']}></div>
                <div className={styles['file-cards']} >
                    {
                        this.props.fileUploadArray.filter((v, idx) => !idx).map(fileUpload => {
                            return <div key={fileUpload.id} className={this.props.cardClasses} data-jkey={`file-list-item-container`}>
                                {
                                    !fileUpload.permanentlyDeleted && <FileListItemComponent
                                        {...this.props}
                                        fileUpload={fileUpload}>

                                    </FileListItemComponent>
                                }
                            </div>
                        })
                    }
                    {/* Conversion-wait / hand-off unit (AD_PLACEMENT_DESIGN.md §9, 2026-09-10): in the
                        Downloadable Files panel, on screen for the whole wait AND still there when Download
                        enables -- dead time the user already spends here, never over a control. One 300x250
                        covers both moments.
                        R14-S1 (review 6 BUG-06, F2 §5): it used to sit ABOVE the first card, between the
                        "queued · processing" strip and the cards, and at 900 x 700 its 250 px + 18 px of margin
                        (the "270 px empty block at y 288") pushed the newest card to y 585 and its Download
                        button to y 718 -- 18 px under the fold -- while unfilled. The owner's ruling: after an
                        export the result card is the first thing above the fold, and an unfilled ad never
                        reserves space ABOVE the card. The unit now follows the FIRST card (the payoff), ahead of
                        the 970 x 90 that already sat there; the card keeps it on screen through the wait.
                        Gated on fileDownloadPageTop (free=true / paid=false in prod) ON PURPOSE: the more
                        specific fileDownloadPageWithInCurrentDownloads key is NOT declared in
                        MotiveInstant.kt, so professorx never serves it (strict Gson typing) -- the older
                        60px unit on that key in file-download.component.tsx has been dead for the same
                        reason. Adding the field is a shared-lib + professorx deploy; reuse the served
                        top-of-downloads role instead. */}
                    <div className="gcontent-container" style={{ display: 'flex', justifyContent: 'center', margin: '6px 0 12px' }}>
                        <MotiveComponent
                            data={AdSlots.inContent} containerWidth="100%" containerHeight="250px"
                            location={MotiveLocationTypes.fileDownloadPageTop}
                            adWidth="300px" adHeight="250px" showAds={true} className='rect_med'
                        ></MotiveComponent>
                    </div>
                    {
                        <div className="gcontent-container">
                            <MotiveComponent
                                data={AdSlots.inContent} containerWidth="100%" containerHeight="90px"
                                location={MotiveLocationTypes.fileDownloadPageBottom} adWidth="970px" adHeight="90px" showAds={true} className='width_major'
                            ></MotiveComponent>
                        </div>
                    }
                    {
                        this.props.fileUploadArray.filter((v, idx) => idx).map(fileUpload => {
                            return <div key={fileUpload.id} className={this.props.cardClasses} data-jkey={`file-list-item-container`}>
                                {
                                    !fileUpload.permanentlyDeleted && <FileListItemComponent
                                        {...this.props}
                                        fileUpload={fileUpload}>

                                    </FileListItemComponent>
                                }
                            </div>
                        })
                    }
                </div >
                <div className={styles['right-panel']}></div>
            </div >
        </div >
    }
}
